import {
  toWSDateString,
  useWSSnackbar,
  WSAvatar,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar,
  WSTable,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { getPathnameMyResourcesTab, PATH_MY_RESOURCES } from "../paths";
import {
  DataViewGrid,
  DataViewSection
} from "../../../shared/components/DataView";
import { WSQueries } from "../../../query/WSQuery";
import { selectorInsuranceCoverageStatusPillByExpirationDate } from "../selectors/selectorInsuranceCoverageStatusPillByExpirationDate";
import { selectorInsuranceCoverageStatusDescriptionByInsurance } from "../selectors/selectorInsuranceCoverageStatusDescriptionByInsurance";
import { useGetNow } from "../../../shared/hooks/useGetNow";
import { InsuranceCoverage } from "@wingspanhq/wingspan-api-sdk";
import { useHistory } from "react-router-dom";
import { MyResourcesTab } from "../types";
import { queriesInsuranceImports } from "../../../v3/query/queriesInsuranceImports";
import { selectorPolicyTypeByPolicyType } from "../selectors/selectorPolicyTypeByPolicyType";
import { MyResourceInsuranceImportHistory } from "../components/MyResourceInsuranceImportHistory";
import { mapInsuranceCoverageToDataView } from "../../Requirements/modules/SharedFile/configs/helpers/mapInsuranceCoverageToDataView";
import { mapInsuranceLimitsToTableData } from "../../Requirements/modules/SharedFile/configs/helpers/mapInsuranceLimitsToTableData";
import { useMutationDownloadVaultFile } from "../../../v3/utils/useMutationDownloadVaultFile";

type Props = {
  insuranceCoverage: InsuranceCoverage;
  tab?: MyResourcesTab;
};

export const InsuranceImportDetails: React.FC<Props> = ({
  insuranceCoverage,
  tab
}) => {
  const history = useHistory();
  const qInsuranceImport = queriesInsuranceImports.getInsuranceImport.useQuery(
    { importId: insuranceCoverage.sourceId! },
    { enabled: !!insuranceCoverage.sourceId }
  );

  const now = useGetNow();
  const { openSnackbar } = useWSSnackbar();
  const [downloadVaultFile] = useMutationDownloadVaultFile();

  return (
    <WSPage colorBackground="gray50">
      <WSQueries queries={{ qInsuranceImport }}>
        {({ qInsuranceImportData }) => {
          const name = insuranceCoverage?.title ?? undefined;

          const handleSourceDownload = async (fileId: string) => {
            await downloadVaultFile(
              { fileId },
              {
                onSuccess: () => {
                  openSnackbar({
                    message: "File downloaded successfully",
                    type: "success"
                  });
                },
                onError: () => {
                  openSnackbar({
                    message: "Download failed",
                    type: "warning"
                  });
                }
              }
            );
          };

          return (
            <>
              <WSPageToolbar
                title={name}
                actions={[
                  {
                    label: "View document",
                    buttonKind: "Primary",
                    onClick: () => {
                      handleSourceDownload(
                        qInsuranceImportData.fileId as string
                      );
                    }
                  }
                ]}
                breadcrumbs={[
                  {
                    label: "My resources",
                    onClick: () => {
                      if (tab) {
                        return history.push(getPathnameMyResourcesTab(tab));
                      } else {
                        history.push(PATH_MY_RESOURCES);
                      }
                    }
                  },
                  {
                    label: selectorPolicyTypeByPolicyType(
                      insuranceCoverage.policyType
                    )
                  }
                ]}
              />
              <WSGrid>
                <WSGrid.Item span={{ s: "12", m: "8" }}>
                  <WSList gap="XL">
                    <DataViewSection
                      gridColumns={3}
                      title="Policy information"
                      data={[
                        {
                          label: "Insurer",
                          value: insuranceCoverage.carrier
                        },
                        {
                          label: "Policy number",
                          value: insuranceCoverage.policyNumber
                        },
                        {
                          label: "Status",
                          pill: selectorInsuranceCoverageStatusPillByExpirationDate(
                            insuranceCoverage.expirationDate
                          ),
                          value: null,
                          hideValue: true,
                          secondaryText:
                            selectorInsuranceCoverageStatusDescriptionByInsurance(
                              insuranceCoverage,
                              now
                            )
                        }
                      ]}
                    ></DataViewSection>
                    <WSPanel>
                      <DataViewGrid
                        data={mapInsuranceCoverageToDataView(
                          insuranceCoverage,
                          { showBasicInfo: false, showLimits: false }
                        )}
                        mb="L"
                      />
                      <WSText.ParagraphSm weight="medium" mb="S">
                        Limits
                      </WSText.ParagraphSm>
                      <WSTable<any>
                        tableData={mapInsuranceLimitsToTableData(
                          insuranceCoverage
                        )}
                        columns={[
                          {
                            config: {
                              gridTemplateSizeMax: "999px"
                            },
                            renderFunction: ({ data }) => (
                              <WSText.ParagraphSm weight="medium">
                                {data.label}
                              </WSText.ParagraphSm>
                            )
                          },
                          {
                            config: {
                              gridTemplateSizeMax: "999px"
                            },
                            renderFunction: ({ data }) => (
                              <WSText.ParagraphXs weight="light">
                                {data.value}
                              </WSText.ParagraphXs>
                            )
                          }
                        ]}
                      />
                    </WSPanel>
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ s: "12", m: "4" }}>
                  <WSList gap="XL">
                    <WSElement>
                      <WSSectionToolbar title="Source" />
                      <WSCard
                        mt="S"
                        divider
                        actions={{
                          alignment: "fill",
                          buttons: [
                            {
                              label: "View document",
                              kind: "Secondary",
                              onClick: () => {
                                handleSourceDownload(
                                  qInsuranceImportData.fileId as string
                                );
                              }
                            }
                          ]
                        }}
                      >
                        <WSList gap="L">
                          <WSFlexBox.CenterY gap="M" wrap="nowrap">
                            <WSAvatar.Icon
                              icon="upload"
                              colorBackground="gray500"
                            />
                            <WSText.Paragraph weight="medium">
                              {name}
                            </WSText.Paragraph>
                          </WSFlexBox.CenterY>

                          <WSInfoBox>
                            This policy was extracted and imported from an
                            insurance document uploaded by you on{" "}
                            {toWSDateString(
                              insuranceCoverage.events?.createdAt
                            )}
                          </WSInfoBox>
                        </WSList>
                      </WSCard>
                    </WSElement>
                    <MyResourceInsuranceImportHistory
                      insuranceImportId={qInsuranceImportData.id!}
                      isCompactView={true}
                      insuranceCoverageId={insuranceCoverage.id!}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
